import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MessageCircle, Send, MapPin, Clock, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      title: "Email",
      value: "support@voiphub.in",
      link: "mailto:support@voiphub.in",
      description: "For general inquiries"
    },
    {
      icon: Phone,
      title: "Phone",
      value: "+91-XXXXXXXXXX",
      link: "tel:+91XXXXXXXXXX",
      description: "Available during business hours"
    },
    {
      icon: MessageCircle,
      title: "Telegram",
      value: "@voiphub_support",
      link: "https://t.me/voiphub_support",
      description: "Fastest response - 24/7 support"
    }
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 noise-bg" data-testid="contact-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-4">Get In Touch</h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Have questions? We're here to help. Reach out through any channel below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="section-light -mt-12 relative z-10" data-testid="contact-methods">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {contactMethods.map((method, i) => (
              <motion.a
                key={i}
                href={method.link}
                target={method.title === "Telegram" ? "_blank" : undefined}
                rel={method.title === "Telegram" ? "noopener noreferrer" : undefined}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all text-center group"
                data-testid={`contact-${method.title.toLowerCase()}`}
              >
                <div className="w-16 h-16 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-brand-500 transition-colors">
                  <method.icon className="w-8 h-8 text-brand-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-1">{method.title}</h3>
                <p className="text-brand-500 font-medium mb-2">{method.value}</p>
                <p className="text-slate-500 text-sm">{method.description}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-alt" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Send Us a Message
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Fill out the form and our team will get back to you within 24 hours. For faster response, contact us directly on Telegram.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-brand-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Global Service</h4>
                    <p className="text-slate-600">We operate globally with no physical office. All support is provided remotely.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-brand-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">24/7 Support</h4>
                    <p className="text-slate-600">Our team is available around the clock on Telegram for urgent matters.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-brand-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Fastest Response</h4>
                    <p className="text-slate-600">For immediate assistance, reach us on Telegram @voiphub_support</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-10">
                <a
                  href="https://t.me/voiphub_support"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6" data-testid="telegram-cta-btn">
                    <MessageCircle className="mr-2 w-5 h-5" />
                    Chat on Telegram
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200"
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="John Doe"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="john@company.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="Company Name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    placeholder="How can we help you?"
                    rows={5}
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-brand-500 hover:bg-brand-600 text-white rounded-full py-6 font-semibold"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send className="mr-2 w-4 h-4" />
                      Send Message
                    </>
                  )}
                </Button>
                
                <p className="text-center text-sm text-slate-500">
                  We typically respond within 24 hours
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
